import type { GigRequestStatus, TeenGigRequestListItemResponse } from "../../../types/gigTypes";
import { formatDateTimeInGigTimeZone } from "../../../utils/DateUtils";
import { formatTeenGigCompensation } from "../../../utils/GigCompensationUtils";
import { formatGigScheduleSummary } from "../../../utils/GigScheduleUtils";
import { ACTIVE_REQUEST_STATUSES } from "./constants";

export const requestStatusDescription: Record<GigRequestStatus, string> = {
  GUARDIAN_PENDING: "Waiting on parent/guardian approval for this gig.",
  POSTER_PENDING: "Waiting on the poster.",
  POSTER_SELECTED_PENDING_CREW: "Selected by the poster. Waiting for the full crew before payment is confirmed.",
  PAYOUT_SETUP_PENDING: "The poster selected you. Finish payout setup so the gig can be funded and fully confirmed.",
  FUNDING_PENDING: "Your payout setup is ready. Waiting on the poster to pay and fully confirm the gig.",
  FILLED_BY_OTHER_TEEN: "Filled by another teen.",
  GUARDIAN_REJECTED: "Declined by a guardian.",
  POSTER_REJECTED: "Declined by the poster.",
  EXPIRED: "This gig expired before a decision was made.",
  WITHDRAWN: "You withdrew this request.",
  REMOVED_BY_POSTER: "The poster removed you from this scheduled gig.",
  REMOVED_BY_TEEN: "You left this scheduled gig.",
  POSTER_ACCEPTED: "Moved to scheduled gigs.",
  CANCELLED: "This gig was cancelled.",
};

export const toLocation = (gig: TeenGigRequestListItemResponse) =>
  [gig.city, gig.state, gig.zip].filter(Boolean).join(" ") || "Location TBD";

export const toSchedule = (gig: TeenGigRequestListItemResponse) => {
  if (gig.gigScheduleType) {
    const scheduleSummary = formatGigScheduleSummary({
      scheduleType: gig.gigScheduleType,
      scheduleNotes: gig.gigScheduleNotes,
      startTime: gig.gigStartTime,
      endTime: gig.gigEndTime,
      preferredStartTime: gig.gigPreferredStartTime,
      preferredEndTime: gig.gigPreferredEndTime,
      timeZone: gig.gigTimeZone,
    });

    if (scheduleSummary !== "Schedule to be confirmed") {
      return scheduleSummary;
    }
  }

  if (gig.confirmedStartTime) {
    return `${formatDateTimeInGigTimeZone(gig.confirmedStartTime, gig.gigTimeZone)} to ${formatDateTimeInGigTimeZone(gig.confirmedEndTime, gig.gigTimeZone)}`;
  }

  if (gig.proposedStartTime || gig.proposedEndTime) {
    return `${formatDateTimeInGigTimeZone(gig.proposedStartTime, gig.gigTimeZone)} to ${formatDateTimeInGigTimeZone(gig.proposedEndTime, gig.gigTimeZone)}`;
  }

  return "Schedule not proposed yet";
};

export const getRequestStageLabel = (status: GigRequestStatus) =>
  ACTIVE_REQUEST_STATUSES.includes(status) ? "Active" : "Past";

export const getRequestStageClassName = (status: GigRequestStatus) =>
  ACTIVE_REQUEST_STATUSES.includes(status)
    ? "border-emerald-200 bg-emerald-50 text-emerald-800"
    : "border-slate-200 bg-slate-100 text-slate-700";

export const formatGigPayment = (paymentAmountCents: number | null, volunteer: boolean) =>
  formatTeenGigCompensation(paymentAmountCents, volunteer);

export const isAwaitingTeenScheduleConfirmation = (gig: TeenGigRequestListItemResponse) =>
  gig.status === "POSTER_PENDING" && gig.latestProposalByRole === "POSTER" && gig.teenConfirmedPosterTimeAt === null;

export const buildRequestSummary = (gigs: TeenGigRequestListItemResponse[]) => ({
  total: gigs.length,
  active: gigs.filter((gig) => ACTIVE_REQUEST_STATUSES.includes(gig.status)).length,
  awaitingConfirmation: gigs.filter((gig) => isAwaitingTeenScheduleConfirmation(gig)).length,
  closed: gigs.filter(
    (gig) =>
      gig.status === "POSTER_REJECTED"
      || gig.status === "GUARDIAN_REJECTED"
      || gig.status === "FILLED_BY_OTHER_TEEN"
      || gig.status === "EXPIRED",
  ).length,
  withdrawn: gigs.filter((gig) => gig.status === "WITHDRAWN").length,
});

export const getEmptyStateCopy = (filter: "ACTIVE" | "AWAITING_CONFIRMATION" | "PAST" | "ALL") => {
  if (filter === "ACTIVE") {
    return {
      title: "No active gigs applied for right now",
      body: "Gigs waiting on parent/guardian gig approval or a poster decision will show up here.",
    };
  }

  if (filter === "AWAITING_CONFIRMATION") {
    return {
      title: "No gigs are waiting on your schedule confirmation",
      body: "When a gig poster suggests the latest time and it is your turn to respond, those requests will show up here.",
    };
  }

  if (filter === "PAST") {
    return {
      title: "No past gigs applied for to review",
      body: "Withdrawn, declined, and filled-by-another-teen gigs will show up here.",
    };
  }

  return {
    title: "No gigs applied for yet",
    body: "Once you apply for gigs that are still pending, rejected, or withdrawn, they will appear here.",
  };
};
